import React, { useLayoutEffect, useRef, useState } from 'react';
import { RobotOutlined, SendOutlined } from '@ant-design/icons';
import { appear } from './AutoDemo';
import logo from '../../assets/tendera_logo_transparent.png';
import './demoKit.css';

// Building blocks shared by the coded demos: the app's frame, the AI panel, answers and the closing card.

/** The app with its dark sidebar; `active` is the highlighted navigation item. */
export const AppFrame = ({ active, children }) => (
    <div className="dk-app">
        <div className="dk-side">
            <img src={logo} alt="" className="dk-side__logo" />
            {[0, 1, 2, 3].map((i) => <span key={i} className="dk-side__item" />)}
            <span className="dk-side__item is-active">{active}</span>
        </div>
        <div className="dk-main">{children}</div>
    </div>
);

/** A page drawn as grey placeholders behind a dialog. */
export const PlaceholderRows = ({ title }) => (
    <>
        <div className="dk-main__title">{title}</div>
        <div className="dk-main__filters">{[0, 1, 2, 3].map((i) => <span key={i} />)}</div>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="dk-main__row"><span /><span /><span /></div>)}
    </>
);

/**
 * The AI chat panel. `scroll` (0–1) scrolls the conversation by however much it overflows, so a scene works for
 * every language and row count.
 */
export const AssistantPanel = ({ title, tabs = [], placeholder, style, input, sending, children, scroll = 0, className = '' }) => {
    const bodyRef = useRef(null);
    const contentRef = useRef(null);
    const [overflow, setOverflow] = useState(0);
    useLayoutEffect(() => {
        const next = Math.max(0, (contentRef.current?.scrollHeight || 0) - (bodyRef.current?.clientHeight || 0) + 14);
        if (next !== overflow) setOverflow(next);
    });
    return (
        <div className={`dk-panel ${className}`} style={style}>
            <div className="dk-panel__head">
                <span className="dk-panel__title"><RobotOutlined /> {title}</span>
                {tabs.length > 0 && <span className="dk-panel__tabs">{tabs.map((tab, i) => <span key={tab} className={i === 0 ? 'is-on' : ''}>{tab}</span>)}</span>}
            </div>
            <div className="dk-panel__body" ref={bodyRef}>
                <div ref={contentRef} style={{ transform: `translateY(${-scroll * overflow}px)` }}>{children}</div>
            </div>
            <div className="dk-panel__input">
                <span className={input ? '' : 'is-placeholder'}>{input || placeholder}{input ? <i className="dk-caret" /> : null}</span>
                <span className={`dk-panel__send${sending ? ' is-on' : ''}`}><SendOutlined /></span>
            </div>
        </div>
    );
};

/** The closing card: logo and up to three lines, the first in orange. No prices: they would date the demo. */
export const EndCard = ({ t, lines }) => (
    <div className="dk-end">
        <img src={logo} alt="" className="dk-end__logo" style={appear(t, 100, 500)} />
        {lines.map((line, i) => (
            <div key={line} className={`dk-end__line${i === 0 ? ' is-accent' : ''}`} style={appear(t, 700 + i * 650, 450)}>{line}</div>
        ))}
    </div>
);
